import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { PreferencesProvider } from "./contexts/PreferencesContext";
import { CatalystFlowProvider } from "./contexts/CatalystFlowContext";
import { AskCatalystFlowPanel } from "./components/catalyst-flow";
import { useSiteSettings } from "./hooks/useSiteSettings";
import { useAdminAuth } from "./hooks/useAdminAuth";
import { isAppSubdomain } from "./lib/subdomain";
import { PageLoader } from "@/components/ui/page-loader";

// HomePage and MarketingLayout eager loaded for LCP optimization
import HomePage from "./pages/marketing/HomePage";
import { MarketingLayout } from "./components/layout/MarketingLayout";

// Other marketing pages lazy loaded
const PricingPage = lazy(() => import("./pages/marketing/PricingPage"));
const FeaturesPage = lazy(() => import("./pages/marketing/FeaturesPage"));
const ContactPage = lazy(() => import("./pages/marketing/ContactPage"));
const ShippingPolicy = lazy(() => import("./pages/marketing/ShippingPolicy"));
const TermsConditions = lazy(() => import("./pages/marketing/TermsConditions"));
const CancellationRefunds = lazy(() => import("./pages/marketing/CancellationRefunds"));
const PrivacyPolicy = lazy(() => import("./pages/marketing/PrivacyPolicy"));

// Keep layout as eager import for instant shell rendering
import { AppLayout } from "./components/layout/AppLayout";

// Lazy load all page components for optimal bundle splitting
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Sales = lazy(() => import("./pages/Sales"));
const SuperQueue = lazy(() => import("./pages/SuperQueue"));
const TaskFlow = lazy(() => import("./pages/TaskFlow"));
const Auth = lazy(() => import("./pages/Auth"));
const ProjectsClients = lazy(() => import("./pages/ProjectsClients"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Finance = lazy(() => import("./pages/Finance"));
const Calendar = lazy(() => import("./pages/Calendar"));

const Settings = lazy(() => import("./pages/Settings"));
const Archive = lazy(() => import("./pages/Archive"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Forbidden = lazy(() => import("./pages/Forbidden"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const KPIDashboard = lazy(() => import("./pages/admin/KPIDashboard"));
const UsagePage = lazy(() => import("./pages/admin/UsagePage"));
const SecurityPage = lazy(() => import("./pages/admin/SecurityPage"));
const UsersPage = lazy(() => import("./pages/admin/UsersPage"));
const AdminFinancePage = lazy(() => import("./pages/admin/FinancePage"));
const CreditsMonitor = lazy(() => import("./pages/admin/CreditsMonitor"));
const RegionalPricing = lazy(() => import("./pages/admin/RegionalPricing"));
const AdminSettingsPage = lazy(() => import("./pages/admin/SettingsPage"));
const Maintenance = lazy(() => import("./pages/Maintenance"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000, // 10 minutes - keep data fresh longer
      gcTime: 30 * 60 * 1000, // 30 minutes - retain in cache longer
      refetchOnWindowFocus: false,
      refetchOnMount: false, // 🔥 KEY: Don't refetch on mount if data exists
      retry: 1, // Reduce retry attempts for faster failures
    },
  },
});

const ProtectedAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { session, isLoading: authLoading } = useAuth();
  const { isAdmin, isLoading: adminLoading } = useAdminAuth();

  if (authLoading || adminLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <PageLoader />
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
};

const AppRoutes = () => {
  const { session, isLoading } = useAuth();
  const { data: siteSettings } = useSiteSettings();
  const { isAdmin } = useAdminAuth();
  const onAppSubdomain = isAppSubdomain();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <PageLoader />
      </div>
    );
  }

  // If on app.catlyo.com and not logged in, redirect to auth
  // If on app.catlyo.com and logged in but on root, redirect to dashboard
  if (onAppSubdomain && window.location.pathname === "/") {
    if (!session) {
      return <Navigate to="/auth" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Suspense fallback={<div className="flex h-screen items-center justify-center bg-background"><PageLoader /></div>}>
      <Routes>
        {/* Public Marketing Routes - blocked during maintenance for non-admins */}
        {[
          { path: "/", element: <HomePage /> },
          { path: "/features", element: <FeaturesPage /> },
          { path: "/pricing", element: <PricingPage /> },
          { path: "/contact", element: <ContactPage /> },
          { path: "/shipping-policy", element: <ShippingPolicy /> },
          { path: "/terms", element: <TermsConditions /> },
          { path: "/cancellation-refunds", element: <CancellationRefunds /> },
          { path: "/privacy", element: <PrivacyPolicy /> },
        ].map(({ path, element }) => (
          <Route
            key={path}
            path={path}
            element={
              siteSettings?.maintenance_mode && !isAdmin ? (
                <Maintenance message={siteSettings.maintenance_message} />
              ) : (
                <MarketingLayout>{element}</MarketingLayout>
              )
            }
          />
        ))}

        {/* Auth Routes */}
        <Route path="/auth" element={!session ? <Auth /> : <Navigate to="/dashboard" />} />
        <Route path="/forbidden" element={<Forbidden />} />
        
        {/* Admin Routes */}
        <Route
          path="/admin"
          element={
            <ProtectedAdminRoute>
              <AdminDashboard />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/kpis"
          element={
            <ProtectedAdminRoute>
              <KPIDashboard />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/usage"
          element={
            <ProtectedAdminRoute>
              <UsagePage />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/security"
          element={
            <ProtectedAdminRoute>
              <SecurityPage />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/finance"
          element={
            <ProtectedAdminRoute>
              <AdminFinancePage />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedAdminRoute>
              <UsersPage />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/credits"
          element={
            <ProtectedAdminRoute>
              <CreditsMonitor />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/regional-pricing"
          element={
            <ProtectedAdminRoute>
              <RegionalPricing />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedAdminRoute>
              <AdminSettingsPage />
            </ProtectedAdminRoute>
          }
        />
        
        {/* Protected App Routes - with maintenance mode check */}
        {[
          { path: "/dashboard", element: <Dashboard /> },
          { path: "/inbox", element: <SuperQueue /> },
          { path: "/sales", element: <Sales /> },
          { path: "/tasks", element: <TaskFlow /> },
          { path: "/projects-clients", element: <ProjectsClients /> },
          { path: "/invoices", element: <Invoices /> },
          { path: "/finance", element: <Finance /> },
          { path: "/calendar", element: <Calendar /> },
          { path: "/settings", element: <Settings /> },
          { path: "/archive", element: <Archive /> },
        ].map(({ path, element }) => (
          <Route
            key={path}
            path={path}
            element={
              session ? (
                siteSettings?.maintenance_mode && !isAdmin ? (
                  <Maintenance message={siteSettings.maintenance_message} />
                ) : (
                  <AppLayout>{element}</AppLayout>
                )
              ) : (
                <Navigate to="/auth" />
              )
            }
          />
        ))}

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CatalystFlowProvider>
          <BrowserRouter>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <AppRoutes />
              <AskCatalystFlowPanel />
            </TooltipProvider>
          </BrowserRouter>
        </CatalystFlowProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
